import React, { useCallback } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';

import { Button, DataCard, Typography } from '@vivaahealth/design-system';

import { INITIAL_RTE_STATE, RteState } from '../../types';
import { getInsuranceCardData } from '../../utils';

import * as api from '../../../../../../api';

interface RteErrorReviewWithDataCardProps extends WithTranslation {
  setRteState: React.Dispatch<React.SetStateAction<RteState>>;
  cards: api.PatientInsuranceInformation.InsuranceInformationWithDetails[];
  patientInsuranceInformationId: string;
  payorsDisplayNameMapping: Record<string, string>;
  onAddInsurance: () => void;
  onCancel: () => Promise<void>;
}

const RteErrorReviewDataCard = ({
  setRteState,
  cards,
  patientInsuranceInformationId,
  payorsDisplayNameMapping,
  onAddInsurance,
  onCancel,
  t,
}: RteErrorReviewWithDataCardProps) => {
  const location = useLocation();

  const handleClickDoNotAddThisCard = useCallback(() => {
    setRteState(INITIAL_RTE_STATE);
    onCancel();
  }, [setRteState, onCancel]);

  const erroredCard = cards.find(
    (card) => card.id === patientInsuranceInformationId,
  );

  if (!erroredCard) {
    return <Redirect to={'./' + location.search} />;
  }

  const {
    insuranceProviderImgSrc,
    memberId,
    groupNumber,
    coverageStartDate,
    payorDisplayName,
  } = getInsuranceCardData(erroredCard, payorsDisplayNameMapping);

  return (
    <>
      <DataCard
        cardHeaderLeft={
          !!insuranceProviderImgSrc ? (
            <img
              src={insuranceProviderImgSrc}
              alt={t('logoOfInsuranceProvider', { ns: 'insurance' })}
              height={40}
            />
          ) : (
            <Typography variant="title-medium" bold>
              {t(payorDisplayName)}
            </Typography>
          )
        }
        rows={[
          {
            rowHeader: t('memberId', { ns: 'insurance' }),
            rowData: memberId,
          },
          {
            rowHeader: t('groupNumber', { ns: 'insurance' }),
            rowData: groupNumber,
          },
          {
            rowHeader: t('coverageStartDate', { ns: 'insurance' }),
            rowData: coverageStartDate,
          },
        ]}
      />
      <Button fullWidth variant="primary" onClick={onAddInsurance}>
        {t('addNewCard', { ns: 'uploadCard' })}
      </Button>
      <Button
        fullWidth
        variant="outlined"
        onClick={handleClickDoNotAddThisCard}
      >
        {t('doNotAddThisCard', { ns: 'insurance' })}
      </Button>
    </>
  );
};

export default withTranslation()(RteErrorReviewDataCard);
